import React, { useRef, useEffect, useState, useCallback } from 'react';
import getImageUrl from './getImageUrl';
import useNodeSize from './useNodeSize';
import usePixelRatio from './usePixelRatio';
import useHasObjectFit from './useHasObjectFit';
function calculateBounds(width, height, containerWidth, containerHeight) {
    const imageRatio = width / height;
    const containerRatio = containerWidth / containerHeight;
    if (imageRatio > containerRatio) {
        const heightPercent = containerRatio / imageRatio;
        return [0, (1 - heightPercent) / 2, 1, heightPercent];
    }
    const widthPercent = imageRatio / containerRatio;
    return [(1 - widthPercent) / 2, 0, widthPercent, 1];
}
export function CoverResizeImage(props) {
    const { src, size, maxSize, quality, focus, fit = 'cover', className, style, innerClassName, innerStyle, children, } = props;
    const nodeRef = useRef(null);
    const imageRef = useRef(null);
    const pixelRatio = usePixelRatio();
    const hasObjectFit = useHasObjectFit();
    const actualSize = useNodeSize(nodeRef, size);
    const [imageSize, setImageSize] = useState([1, 1]);
    function getUrl(multiplier = 1) {
        if (actualSize == null)
            return undefined;
        const nextSize = actualSize.map((v, i) => {
            const value = v * multiplier;
            if (maxSize != null && maxSize[i] < value)
                return maxSize[i];
            return value;
        });
        return getImageUrl({
            src,
            width: nextSize[0],
            height: nextSize[1],
            mode: 'output',
            quality,
        });
    }
    const imageUrl = getUrl(1);
    const srcSet = actualSize != null && src != null
        ? [1.5, 2, 3].map((size) => `${getUrl(size)} ${size}x`).join(',')
        : undefined;
    const handleImageLoad = useCallback(() => {
        const image = imageRef.current;
        if (image == null)
            return;
        setImageSize([image.naturalWidth, image.naturalHeight]);
    }, []);
    useEffect(() => {
        if (hasObjectFit)
            return;
        const url = getUrl(pixelRatio);
        if (url == null)
            return;
        const image = new Image();
        image.onloadedmetadata = () => {
            setImageSize([image.naturalWidth, image.naturalHeight]);
        };
        image.onload = () => {
            setImageSize([image.naturalWidth, image.naturalHeight]);
        };
        image.src = url;
        return () => {
            image.src = '';
        };
    }, [imageUrl, hasObjectFit, pixelRatio]);
    return (React.createElement("div", { ref: nodeRef, className: className, style: {
            position: 'relative',
            ...(style ?? {}),
        } },
        hasObjectFit ? (React.createElement("img", { className: innerClassName, src: imageUrl, srcSet: srcSet, onLoadedMetadata: handleImageLoad, onLoad: handleImageLoad, ref: imageRef, style: {
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%',
                objectFit: fit,
                objectPosition: focus != null
                    ? `${focus[0] * 100}% ${focus[1] * 100}%`
                    : 'center center',
                ...(innerStyle ?? {}),
            } })) : (React.createElement("div", { className: innerClassName, style: {
                position: 'absolute',
                top: '0',
                left: '0',
                width: '100%',
                height: '100%',
                backgroundImage: `url(${getUrl(pixelRatio)})`,
                backgroundSize: fit,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: focus != null
                    ? `${focus[0] * 100}% ${focus[1] * 100}%`
                    : 'center center',
                ...(innerStyle ?? {}),
            } })),
        typeof children === 'function'
            ? children({
                width: imageSize[0],
                height: imageSize[1],
                containerWidth: actualSize != null ? actualSize[0] : 0,
                containerHeight: actualSize != null ? actualSize[1] : 0,
                bounds: calculateBounds(imageSize[0], imageSize[1], actualSize != null ? actualSize[0] : 0, actualSize != null ? actualSize[1] : 0),
            })
            : children));
}
//# sourceMappingURL=CoverResizeImage.js.map